import { Address, parseAbi } from "viem";

const configuredAddress = (value: string | undefined): Address | undefined =>
  value?.match(/^0x[a-fA-F0-9]{40}$/) ? value as Address : undefined;

export const UNIVERSAL_IDENTITY_REGISTRY_ADDRESS = configuredAddress(
  import.meta.env.VITE_UNIVERSAL_IDENTITY_REGISTRY_ADDRESS,
);
export const ERC8004_IDENTITY_REGISTRY_ADDRESS = configuredAddress(
  import.meta.env.VITE_ERC8004_IDENTITY_REGISTRY_ADDRESS,
);
export const ERC8004_REPUTATION_REGISTRY_ADDRESS = configuredAddress(
  import.meta.env.VITE_ERC8004_REPUTATION_V2_REGISTRY_ADDRESS || import.meta.env.VITE_ERC8004_REPUTATION_REGISTRY_ADDRESS,
);
export const ERC8004_VALIDATION_REGISTRY_ADDRESS = configuredAddress(
  import.meta.env.VITE_ERC8004_VALIDATION_REGISTRY_ADDRESS,
);
export const INCEPTION_AGENT_BINDING_REGISTRY_ADDRESS = configuredAddress(
  import.meta.env.VITE_INCEPTION_AGENT_BINDING_REGISTRY_ADDRESS,
);
export const PROJECT_REGISTRY_ADDRESS = configuredAddress(
  import.meta.env.VITE_PROJECT_REGISTRY_ADDRESS,
);
export const ENTITY_RELATIONSHIP_REGISTRY_ADDRESS = configuredAddress(
  import.meta.env.VITE_ENTITY_RELATIONSHIP_REGISTRY_ADDRESS,
);
export const CREDENTIAL_REGISTRY_ADDRESS = configuredAddress(
  import.meta.env.VITE_CREDENTIAL_REGISTRY_ADDRESS,
);

export const UNIVERSAL_IDENTITY_ABI = parseAbi([
  "function registerEntity(uint8 entityType, string metadataURI, bytes32 metadataHash) returns (uint256 entityId)",
  "function linkAgent(uint256 entityId, address agentIdentityRegistry, uint256 agentId)",
  "function getEntity(uint256 entityId) view returns ((uint8 entityType,address controller,address pendingController,string metadataURI,bytes32 metadataHash,uint64 registeredAt,bool active))",
  "function controllerOf(uint256 entityId) view returns (address)",
  "event EntityRegistered(uint256 indexed entityId, uint8 indexed entityType, address indexed controller, string metadataURI, bytes32 metadataHash)",
]);

export const PROJECT_REGISTRY_ABI = parseAbi([
  "function registerProject(uint256 founderEntityId,uint8 kind,string metadataURI,bytes32 metadataHash) returns (uint256 projectId)",
  "function bindLaunchAsset(uint256 projectId,address launchAsset)",
  "function getProject(uint256 projectId) view returns ((uint256 founderEntityId,uint8 kind,string metadataURI,bytes32 metadataHash,address launchAsset,uint64 registeredAt,bool active))",
  "function projectForLaunchAsset(address launchAsset) view returns (uint256)",
  "event ProjectRegistered(uint256 indexed projectId,uint256 indexed founderEntityId,uint8 indexed kind,string metadataURI,bytes32 metadataHash)",
  "event LaunchAssetBound(uint256 indexed projectId,address indexed launchAsset)",
]);

export const ENTITY_RELATIONSHIP_REGISTRY_ABI = parseAbi([
  "function proposeRelationship(uint256 sourceEntityId,uint256 subjectEntityId,bytes32 relationshipType,string metadataURI,bytes32 metadataHash,uint64 expiresAt) returns (uint256 relationshipId)",
  "function acceptRelationship(uint256 relationshipId)",
  "function revokeRelationship(uint256 relationshipId)",
  "function getRelationship(uint256 relationshipId) view returns ((uint256 sourceEntityId,uint256 subjectEntityId,bytes32 relationshipType,string metadataURI,bytes32 metadataHash,uint64 proposedAt,uint64 acceptedAt,uint64 expiresAt,uint64 revokedAt))",
  "function isValid(uint256 relationshipId) view returns (bool)",
  "event RelationshipProposed(uint256 indexed relationshipId,uint256 indexed sourceEntityId,uint256 indexed subjectEntityId,bytes32 relationshipType,string metadataURI,bytes32 metadataHash,uint64 expiresAt)",
  "event RelationshipAccepted(uint256 indexed relationshipId,uint64 acceptedAt)",
  "event RelationshipRevoked(uint256 indexed relationshipId,address indexed revokedBy,uint64 revokedAt)",
]);

export const CREDENTIAL_REGISTRY_ABI = parseAbi([
  "function issueCredential(uint256 issuerEntityId,uint256 subjectEntityId,bytes32 credentialType,bytes32 scope,string credentialURI,bytes32 evidenceHash,uint64 expiresAt) returns (uint256 credentialId)",
  "function revokeCredential(uint256 credentialId)",
  "function getCredential(uint256 credentialId) view returns ((uint256 issuerEntityId,uint256 subjectEntityId,bytes32 credentialType,bytes32 scope,string credentialURI,bytes32 evidenceHash,uint64 issuedAt,uint64 expiresAt,uint64 revokedAt))",
  "function isValid(uint256 credentialId) view returns (bool)",
  "event CredentialIssued(uint256 indexed credentialId,uint256 indexed issuerEntityId,uint256 indexed subjectEntityId,bytes32 credentialType,bytes32 scope,string credentialURI,bytes32 evidenceHash,uint64 issuedAt,uint64 expiresAt)",
  "event CredentialRevoked(uint256 indexed credentialId,address indexed revokedBy,uint64 revokedAt)",
]);

export const ERC8004_IDENTITY_ABI = parseAbi([
  "function register(string agentURI) returns (uint256 agentId)",
  "function ownerOf(uint256 agentId) view returns (address)",
  "function getAgentWallet(uint256 agentId) view returns (address)",
  "function agentWalletNonces(uint256 agentId) view returns (uint256)",
  "function setAgentWallet(uint256 agentId, address newWallet, uint256 deadline, bytes signature)",
  "event Registered(uint256 indexed agentId, string agentURI, address indexed owner)",
]);

export const INCEPTION_AGENT_BINDING_ABI = parseAbi([
  "function bind(uint256 entityId, uint256 agentId, address inceptionToken, uint256 inceptionTokenId) returns (address vault)",
  "function bindValidated(uint256 entityId, uint256 agentId, address inceptionToken, uint256 inceptionTokenId, bool capabilitiesValidated) returns (address vault)",
  "function syncBinding(uint256 agentId) returns (bool valid)",
  "function revalidate(uint256 agentId, bool walletProofValidated, bool capabilitiesValidated)",
  "function isBindingValid(uint256 agentId) view returns (bool)",
  "function getBinding(uint256 agentId) view returns ((uint256 bindingId,uint64 controllerEpoch,uint256 entityId,address inceptionToken,uint256 inceptionTokenId,address vault,address controller,uint64 boundAt,uint64 invalidatedAt,uint64 revalidatedAt,bool walletProofValidated,bool capabilitiesValidated,bool active) binding, bool valid)",
  "function bindingHistory(uint256 agentId) view returns (uint256[])",
  "function getBindingById(uint256 bindingId) view returns ((uint256 bindingId,uint64 controllerEpoch,uint256 entityId,address inceptionToken,uint256 inceptionTokenId,address vault,address controller,uint64 boundAt,uint64 invalidatedAt,uint64 revalidatedAt,bool walletProofValidated,bool capabilitiesValidated,bool active))",
  "event AgentVaultBound(uint256 indexed agentId,uint256 indexed bindingId,uint64 indexed controllerEpoch,uint256 entityId,address vault,address inceptionToken,uint256 inceptionTokenId,address controller)",
]);

export const ERC8004_WALLET_DOMAIN_NAME = "Matic Moon Agent Identity";
export const ERC8004_WALLET_DOMAIN_VERSION = "1";

export const ERC8004_REPUTATION_V2_ABI = parseAbi([
  "function giveAgentFeedback(uint256 reviewerAgentId,uint256 subjectAgentId,bytes32 evidenceId,(int128 value,uint8 valueDecimals,string tag1,string tag2,string feedbackURI,bytes32 feedbackHash) data)",
  "function evidenceUsed(bytes32 evidenceId) view returns (bool)",
]);

export const ERC8004_VALIDATION_ABI = parseAbi([
  "function validationRequest(address validatorAddress,uint256 agentId,string requestURI,bytes32 requestHash)",
  "function validationResponse(bytes32 requestHash,uint8 response,string responseURI,bytes32 responseHash,string tag)",
]);
